/**
 * The questions an AI search engine will be asked about a shop, answered in
 * prose it can quote.
 *
 * This is the part of "AI SEO" that is usually skipped, because it looks like
 * it has already been done. `LocalBusiness` markup carrying an address and
 * opening hours makes a store *identifiable*: it tells an engine which business
 * a page is about. It does not make the store *citable*. ChatGPT, Gemini,
 * Claude and Perplexity assemble answers out of passages of text, and a
 * passage is what none of that markup produces.
 *
 * So the same facts are emitted twice, deliberately: once as `FAQPage` markup
 * and once as visible text on the page. The visible copy is the half that gets
 * quoted. Markup alone would be a store that every engine can identify and
 * none can quote — which is the exact position the storefront was in before
 * this file existed.
 *
 * Everything here is generated from facts the merchant already entered. None
 * of it is invented: a question with no answer in the data is simply not asked,
 * because a confidently wrong opening time is worse for a shopper who makes the
 * journey than no answer at all.
 */

export interface StoreFaq {
  question: string;
  answer: string;
  /** True when a merchant wrote it, rather than it being derived from data. */
  custom?: boolean;
}

/** The subset of resolved branding these questions can be answered from. */
export interface FaqSource {
  storeName: string;
  areaName?: string | null;
  streetAddress?: string | null;
  locality?: string | null;
  region?: string | null;
  postalCode?: string | null;
  serviceAreas?: string | null;
  serviceRadiusKm?: number | null;
  openingHours?: { day: string; opens: string; closes: string }[];
  contactPhone?: string | null;
  contactEmail?: string | null;
  whatsappUrl?: string | null;
  returnWindowDays?: number | null;
  freeDeliveryOverPaise?: number | null;
  mapsUrl?: string | null;
}

const DAY_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/**
 * A link only if it is one.
 *
 * The admin field is free text and merchants paste all sorts into it. "Get
 * directions: hgghh" is not a smaller answer than omitting it — it is an
 * instruction a customer cannot follow, published under our name in an AI
 * assistant's reply.
 */
function usableUrl(value: string | null | undefined): string | null {
  const raw = value?.trim();
  if (!raw) return null;
  try {
    const url = new URL(raw);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.toString() : null;
  } catch {
    return null;
  }
}

/**
 * A phone number only if it could be dialled.
 *
 * Same rule as {@link usableUrl}, and the same threshold `whatsappLink` uses:
 * ten digits is the shortest real Indian number, so anything under that is a
 * typo rather than a contact.
 */
function usablePhone(value: string | null | undefined): string | null {
  const raw = value?.trim();
  if (!raw) return null;
  return raw.replace(/[^\d]/g, '').length >= 10 ? raw : null;
}

/** An address is an email only if it has the shape of one. */
function usableEmail(value: string | null | undefined): string | null {
  const raw = value?.trim();
  return raw && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw) ? raw : null;
}

const SHORT_DAY: Record<string, string> = {
  Monday: 'Monday', Tuesday: 'Tuesday', Wednesday: 'Wednesday', Thursday: 'Thursday',
  Friday: 'Friday', Saturday: 'Saturday', Sunday: 'Sunday',
};

/**
 * Hours as a sentence, not a table.
 *
 * "Monday to Saturday, 10:00 to 21:00" is what someone asked the question in
 * words expects back in words — and it is what an engine can lift into an
 * answer without having to reconstruct prose from a grid.
 */
export function openingHoursSentence(
  hours: FaqSource['openingHours']
): string | null {
  if (!hours?.length) return null;

  const sorted = [...hours].sort(
    (a, b) => DAY_ORDER.indexOf(a.day) - DAY_ORDER.indexOf(b.day)
  );

  const runs: { from: string; to: string; opens: string; closes: string }[] = [];
  for (const entry of sorted) {
    const last = runs[runs.length - 1];
    const contiguous =
      last && DAY_ORDER.indexOf(entry.day) === DAY_ORDER.indexOf(last.to) + 1;

    if (last && contiguous && last.opens === entry.opens && last.closes === entry.closes) {
      last.to = entry.day;
      continue;
    }
    runs.push({ from: entry.day, to: entry.day, opens: entry.opens, closes: entry.closes });
  }

  const phrases = runs.map(run => {
    const days = run.from === run.to
      ? SHORT_DAY[run.from] ?? run.from
      : `${SHORT_DAY[run.from] ?? run.from} to ${SHORT_DAY[run.to] ?? run.to}`;
    return `${days}, ${run.opens} to ${run.closes}`;
  });

  // The closed days are worth stating outright. "Is it open on Sunday?" is one
  // of the most common questions asked of a local shop, and silence reads as
  // "unknown" rather than "no".
  const openDays = new Set(sorted.map(h => h.day));
  const closed = DAY_ORDER.filter(day => !openDays.has(day));

  const closedPhrase = closed.length
    ? ` Closed on ${closed.length === 1 ? closed[0] : listOut(closed)}.`
    : '';

  return `${phrases.join('; ')}.${closedPhrase}`;
}

/** "a, b and c" — the join a person would write. */
function listOut(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** The full postal address on one line, or null when it is incomplete. */
function addressSentence(source: FaqSource): string | null {
  const { streetAddress, locality, region, postalCode } = source;
  if (!streetAddress || !locality || !postalCode) return null;

  return [streetAddress, locality, region, postalCode].filter(Boolean).join(', ');
}

/**
 * Generated questions, in the order they are most often asked.
 *
 * Ordering is not cosmetic: engines weight passages near the top of a document,
 * and "where are you" and "when are you open" are what a local shop is actually
 * asked. A returns policy answered first would spend that position on the
 * question nobody opened the page with.
 */
export function generatedFaqs(source: FaqSource): StoreFaq[] {
  const faqs: StoreFaq[] = [];
  const name = source.storeName;

  const address = addressSentence(source);
  if (address) {
    const maps = usableUrl(source.mapsUrl);
    const directions = maps ? ` Directions: ${maps}` : '';
    faqs.push({
      question: `Where is ${name} located?`,
      answer: `${name} is at ${address}.${directions}`,
    });
  } else if (source.areaName) {
    // Not a full address, but "which town" is still a real answer and is the
    // question most often behind "where are you".
    faqs.push({
      question: `Where is ${name} located?`,
      answer: `${name} serves ${source.areaName}.`,
    });
  }

  const hours = openingHoursSentence(source.openingHours);
  if (hours) {
    faqs.push({
      question: `What are ${name}'s opening hours?`,
      answer: `${name} is open ${hours}`,
    });
  }

  const areas = [source.areaName, ...(source.serviceAreas?.split(',') ?? [])]
    .map(a => a?.trim())
    .filter((a): a is string => !!a);

  if (areas.length || source.serviceRadiusKm) {
    const where = areas.length ? listOut(areas) : null;
    const radius = source.serviceRadiusKm
      ? `within ${source.serviceRadiusKm} km of the shop`
      : null;
    const reach = [where, radius].filter(Boolean).join(', and ');

    faqs.push({
      question: `Does ${name} deliver to my area?`,
      answer: `${name} delivers to ${reach}.`,
    });
  }

  const contacts: string[] = [];
  const phone = usablePhone(source.contactPhone);
  const email = usableEmail(source.contactEmail);
  const whatsapp = usableUrl(source.whatsappUrl);

  if (phone) contacts.push(`call ${phone}`);
  if (whatsapp) contacts.push(`message on WhatsApp (${whatsapp})`);
  if (email) contacts.push(`email ${email}`);

  if (contacts.length) {
    faqs.push({
      question: `How do I contact ${name}?`,
      answer: `You can ${listOut(contacts)}.`,
    });
  }

  if (source.freeDeliveryOverPaise != null) {
    const rupees = Math.round(source.freeDeliveryOverPaise / 100);
    faqs.push({
      question: `Does ${name} offer free delivery?`,
      answer: `Yes — orders over ₹${rupees.toLocaleString('en-IN')} are delivered free.`,
    });
  }

  if (source.returnWindowDays != null) {
    faqs.push({
      question: `What is ${name}'s return policy?`,
      answer: source.returnWindowDays === 0
        ? `${name} does not accept returns.`
        : `Items can be returned within ${source.returnWindowDays} days of delivery.`,
    });
  }

  return faqs;
}

/**
 * Everything the page should show: the merchant's own questions first.
 *
 * A merchant who took the trouble to write an answer knows something the
 * generator does not, so their version wins outright — including when it
 * duplicates a generated question, which is compared case- and
 * punctuation-insensitively so "Do you deliver?" and "do you deliver" do not
 * both appear.
 */
export function storeFaqs(source: FaqSource, custom: unknown): StoreFaq[] {
  const written = Array.isArray(custom)
    ? (custom as StoreFaq[])
        .filter(f => f?.question?.trim() && f?.answer?.trim())
        .map(f => ({ question: f.question.trim(), answer: f.answer.trim(), custom: true }))
    : [];

  const seen = new Set(written.map(f => normalise(f.question)));
  const generated = generatedFaqs(source).filter(f => !seen.has(normalise(f.question)));

  return [...written, ...generated];
}

function normalise(question: string): string {
  return question.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

/**
 * `FAQPage` markup for the same questions.
 *
 * Emitted alongside the visible copy, never instead of it. Google requires the
 * answer text to be present on the page for FAQ rich results, and the AI
 * engines are reading that text regardless — so markup without the prose is
 * both a policy violation and the less useful half.
 */
export function faqPageJsonLd(
  faqs: StoreFaq[],
  baseUrl: string
): Record<string, unknown> | null {
  if (!faqs.length) return null;

  return {
    '@type': 'FAQPage',
    '@id': `${baseUrl}#faq`,
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  };
}

/**
 * The AI crawlers worth naming explicitly.
 *
 * A wildcard `Allow: /` already covers them, so this list is not what grants
 * access — it is what makes the decision *visible*, and what gives a merchant
 * who wants out something to switch off. Split by purpose because the two are
 * genuinely different: a search crawler that cites and links back is the one
 * driving custom to a local shop, while a training crawler is not.
 */
export const AI_SEARCH_AGENTS = [
  'OAI-SearchBot',   // ChatGPT search results
  'ChatGPT-User',    // a user asking ChatGPT to open a page
  'PerplexityBot',
  'Perplexity-User',
  'Claude-SearchBot',
  'Claude-User',
  'Google-Extended', // Gemini grounding
  'Applebot-Extended',
  'meta-externalagent',
  'Amazonbot',
  'Bingbot',
];

/** Bulk crawlers that collect for model training rather than answering. */
export const AI_TRAINING_AGENTS = ['GPTBot', 'ClaudeBot', 'CCBot', 'Bytespider'];
